import React, { useState, useEffect } from "react";
import "./App.css";

const STORAGE_KEY = "task_manager_tasks";

function App() {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [dueDate, setDueDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState("");
  const [editDueDate, setEditDueDate] = useState("");

  // Load tasks from Local Storage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setTasks(JSON.parse(stored));
    }
  }, []);

  // Save tasks to Local Storage on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  }, [tasks]);

  const addTask = () => {
    if (!title.trim()) return;
    const newTask = {
      id: Date.now(),
      title,
      description,
      priority,
      dueDate,
      status: "Pending",
    };
    setTasks([...tasks, newTask]);
    setTitle("");
    setDescription("");
    setPriority("Medium");
    setDueDate("");
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter((t) => t.id !== id));
  };

  const toggleStatus = (id) => {
    setTasks(
      tasks.map((t) =>
        t.id === id
          ? { ...t, status: t.status === "Pending" ? "Completed" : "Pending" }
          : t
      )
    );
  };

  const startEdit = (task) => {
    setEditId(task.id);
    setEditTitle(task.title);
    setEditDescription(task.description);
    setEditPriority(task.priority);
    setEditDueDate(task.dueDate);
  };

  const saveEdit = (id) => {
    setTasks(
      tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              title: editTitle,
              description: editDescription,
              priority: editPriority,
              dueDate: editDueDate,
            }
          : t
      )
    );
    setEditId(null);
  };

  const downloadTasks = () => {
    const file = new Blob([JSON.stringify(tasks, null, 2)], {
      type: "application/json",
    });
    const element = document.createElement("a");
    element.href = URL.createObjectURL(file);
    element.download = "tasks.json";
    element.click();
  };

  const clearAll = () => {
    setTasks([]);
  };

  // FILTER LOGIC
  const filteredTasks = tasks.filter((t) => {
    const statusMatch = filterStatus === "All" || t.status === filterStatus;
    const priorityMatch =
      filterPriority === "All" || t.priority === filterPriority;
    return statusMatch && priorityMatch;
  });

  // DASHBOARD STATS
  const total = tasks.length;
  const completed = tasks.filter((t) => t.status === "Completed").length;
  const pending = tasks.filter((t) => t.status === "Pending").length;
  const today = new Date().toISOString().split("T")[0];
  const overdue = tasks.filter(
    (t) => t.dueDate && t.dueDate < today && t.status === "Pending"
  ).length;

  const priorityColor = { High: "#e74c3c", Medium: "#f39c12", Low: "#27ae60" };

  return (
    <div className="container">
      <h1>✅ Task Management</h1>

      {/* DASHBOARD */}
      <div className="dashboard">
        <div className="stat-card">
          <span className="stat-number">{total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card completed">
          <span className="stat-number">{completed}</span>
          <span className="stat-label">Completed</span>
        </div>
        <div className="stat-card pending">
          <span className="stat-number">{pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card overdue">
          <span className="stat-number">{overdue}</span>
          <span className="stat-label">Overdue</span>
        </div>
      </div>

      {/* ADD TASK FORM */}
      <div className="form-section">
        <h2>Add New Task</h2>
        <input
          type="text"
          placeholder="Task title *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
        />
        <input
          type="text"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
        />
        <div className="form-row">
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="select"
          >
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="input date"
          />
        </div>
        <button className="btn add" onClick={addTask}>
          + Add Task
        </button>
      </div>

      {/* FILTER BAR */}
      <div className="filter-bar">
        <label>Status:</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="select small"
        >
          <option>All</option>
          <option>Pending</option>
          <option>Completed</option>
        </select>
        <label>Priority:</label>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="select small"
        >
          <option>All</option>
          <option>High</option>
          <option>Medium</option>
          <option>Low</option>
        </select>
        <button className="btn download" onClick={downloadTasks}>
          ⬇ Download
        </button>
        <button className="btn clear" onClick={clearAll}>
          🗑 Clear All
        </button>
      </div>

      {/* TASK LIST */}
      <div className="task-list">
        {filteredTasks.length === 0 && (
          <p className="empty">No tasks found.</p>
        )}
        {filteredTasks.map((task) => (
          <div
            key={task.id}
            className={`task-card ${task.status === "Completed" ? "done" : ""}`}
          >
            {editId === task.id ? (
              // EDIT MODE
              <div className="edit-mode">
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="input"
                />
                <input
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="input"
                />
                <div className="form-row">
                  <select
                    value={editPriority}
                    onChange={(e) => setEditPriority(e.target.value)}
                    className="select"
                  >
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                  <input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    className="input date"
                  />
                </div>
                <button className="btn save" onClick={() => saveEdit(task.id)}>
                  Save
                </button>
                <button className="btn cancel" onClick={() => setEditId(null)}>
                  Cancel
                </button>
              </div>
            ) : (
              // VIEW MODE
              <>
                <div className="task-header">
                  <span
                    className="priority-badge"
                    style={{ background: priorityColor[task.priority] }}
                  >
                    {task.priority}
                  </span>
                  <span
                    className={`status-badge ${
                      task.status === "Completed" ? "status-done" : "status-pending"
                    }`}
                  >
                    {task.status}
                  </span>
                </div>
                <h3 className={task.status === "Completed" ? "strikethrough" : ""}>
                  {task.title}
                </h3>
                {task.description && <p className="task-desc">{task.description}</p>}
                {task.dueDate && (
                  <p className={`due-date ${task.dueDate < today && task.status === "Pending" ? "overdue-text" : ""}`}>
                    📅 Due: {task.dueDate}
                  </p>
                )}
                <div className="task-actions">
                  <button
                    className="btn toggle"
                    onClick={() => toggleStatus(task.id)}
                  >
                    {task.status === "Pending" ? "✔ Complete" : "↩ Undo"}
                  </button>
                  <button className="btn edit" onClick={() => startEdit(task)}>
                    ✏ Edit
                  </button>
                  <button
                    className="btn delete"
                    onClick={() => deleteTask(task.id)}
                  >
                    🗑 Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
